import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const EMAIL_KEY = "ttfm_wishlist_email";

export function getWishlistEmail() {
  try { return localStorage.getItem(EMAIL_KEY) || ""; } catch { return ""; }
}

export default function WishlistButton({ product, variant }) {
  const [email, setEmail] = useState(getWishlistEmail());
  const [saved, setSaved] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [tempEmail, setTempEmail] = useState("");
  const [busy, setBusy] = useState(false);

  // Refresh state when product/variant/email changes
  useEffect(() => {
    if (!email || !product?.id) { setSaved(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/wishlist/by-email?email=${encodeURIComponent(email)}`);
        if (cancelled) return;
        const match = data.find((w) => w.product_id === product.id && (w.variant_id || null) === (variant?.id || null));
        setSaved(!!match);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [email, product?.id, variant?.id]);

  async function persist(newEmail) {
    setBusy(true);
    try {
      if (saved) {
        await api.post("/wishlist/remove", {
          product_id: product.id,
          customer_email: newEmail,
          variant_id: variant?.id || null,
        });
        setSaved(false);
        toast.success("Removed from wishlist.");
      } else {
        await api.post("/wishlist", {
          product_id: product.id,
          customer_email: newEmail,
          variant_id: variant?.id || null,
          variant_name: variant?.name || null,
        });
        setSaved(true);
        toast.success("Saved for later. We'll remind you in 3 days.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setBusy(false);
    }
  }

  function toggle() {
    if (!product?.id) return;
    if (!email) { setPromptOpen(true); return; }
    persist(email);
  }

  async function submitPrompt(e) {
    e.preventDefault();
    const v = (tempEmail || "").trim().toLowerCase();
    if (!v || !/.+@.+\..+/.test(v)) { toast.error("Please enter a valid email."); return; }
    try { localStorage.setItem(EMAIL_KEY, v); } catch { /* localStorage may be disabled */ }
    setEmail(v);
    setPromptOpen(false);
    await persist(v);
  }

  return (
    <>
      <button onClick={toggle} disabled={busy} data-testid="wishlist-button"
        aria-pressed={saved}
        aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
        className={`shrink-0 w-11 h-11 rounded-full border-2 transition flex items-center justify-center ${saved ? "bg-[var(--accent)]/15 border-[var(--accent)] text-[var(--accent)]" : "bg-[#FDFBF7] border-[#EAE4D3] hover:border-[var(--accent)]/60 text-[#2C2924]"} disabled:opacity-60`}>
        <Heart className={`w-5 h-5 transition ${saved ? "fill-[var(--accent)]" : ""}`} />
      </button>

      <Dialog open={promptOpen} onOpenChange={setPromptOpen}>
        <DialogContent className="max-w-sm bg-[#FDFBF7] border-[#EAE4D3] rounded-3xl" data-testid="wishlist-email-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Save for later</DialogTitle>
            <DialogDescription className="text-[#5C5547]">
              We'll send you a gentle reminder in 3 days. No spam — just this piece.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitPrompt} className="space-y-3 mt-1">
            <input type="email" value={tempEmail} onChange={(e) => setTempEmail(e.target.value)} required autoFocus
              placeholder="you@email.com"
              data-testid="wishlist-email-input"
              className="w-full px-4 py-3 rounded-xl bg-[#FDFBF7] border border-[#EAE4D3] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-sm" />
            <button type="submit" data-testid="wishlist-email-submit"
              className="w-full py-3 rounded-full bg-[#2C2924] text-[#FDFBF7] hover:bg-[#1A1815] text-sm">
              Save it
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
